import { useState, useEffect } from "react";
import { BookOpen, FileText, Info, Heart, File } from "lucide-react";
import PrimaryInformation from "../components/PrimaryInformation";
import Academics from "../components/Academics";
import TestScores from "../components/TestScores";
import Preferences from "../components/Preferences";
import Documents from "../components/Documents";
import { getUserProfile } from "../api/ProfileAPI"; // ✅ Import your API function
import { getUserAcademics } from "../api/AcademicAPI";
import { getUserTestScore } from "../api/TestscoreAPI";
import { getUserPrefrences } from "../api/PrefrenceAPI";
import { getAllDocuments } from "../api/DocumentAPI";
import { useLocation } from "react-router-dom";
import Loader from "../../SAPPages/components/Loader/Loader";

const ProfilePage = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("Primary Information");
  const [profileData, setProfileData] = useState(null); // start as null to show loader
  const [academicData, setAcademicData] = useState([]);
  const [preferencesData, setPreferencesData] = useState([]);
  const [testScoreData, setTestScoreData] = useState([]);
  const [documentsData, setDocumentsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromURL = params.get("tab");

    if (tabFromURL) {
      setActiveTab(tabFromURL);
    }
  }, [location.search]);

  const tabs = [
    { name: "Primary Information", icon: Info, hasWarning: true },
    { name: "Academics", icon: BookOpen, hasWarning: true },
    { name: "Test Scores", icon: FileText },
    { name: "Preferences", icon: Heart },
    { name: "Documents", icon: File },
  ];

  // ✅ Fetch profile data on mount
  const fetchProfile = async () => {
    try {
      const user_id = localStorage.getItem("userId");

      const response = await getUserProfile({ user_id });
      const academicResponse = await getUserAcademics({ user_id });
      const testScoreResponse = await getUserTestScore({ user_id });
      const prefrenceResponse = await getUserPrefrences({ user_id });
      const documentsResponse = await getAllDocuments({ user_id });

      setDocumentsData(documentsResponse.data || []);
      setPreferencesData(prefrenceResponse.data || []);
      setTestScoreData(testScoreResponse.data || []);
      setAcademicData(academicResponse.data || []);
      setProfileData(response.data);
    } catch (error) {
      console.error("Failed to fetch profile:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleInputChange = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveSuccess = async () => {
    // ✅ Re-fetch profile to get fresh data after saving
    setLoading(true);
    await fetchProfile();
    setIsEditing(false);
  };

  const renderTabContent = () => {
    const tabProps = {
      profileData,
      isEditing,
      setIsEditing,
      handleInputChange,
      onSaveSuccess: handleSaveSuccess,
    };

    const commonProps = {
      isEditing,
      setIsEditing,
      onSaveSuccess: handleSaveSuccess,
    };

    switch (activeTab) {
      case "Primary Information":
        return <PrimaryInformation {...tabProps} />;
      case "Academics":
        return <Academics {...commonProps} academicData={academicData} />;
      case "Test Scores":
        return <TestScores {...tabProps} testScoreData={testScoreData} />;
      case "Preferences":
        return <Preferences {...tabProps} preferencesData={preferencesData} />;
      case "Documents":
        return <Documents {...tabProps} docData={documentsData} />;
      default:
        return <PrimaryInformation {...tabProps} />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen text-lg">
        <Loader text="Loading Profile..." />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="flex justify-center items-center min-h-screen text-red-500">
        Failed to load profile data.
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="flex mx-auto">
        <main className="flex-1 p-2">
          <div className="mb-4 flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-medium text-black mb-2 font-siromi">
                My Profile
              </h1>
              {isEditing && (
                <div className="flex items-center text-base text-black gap-2">
                  <Info className="rotate-180" />
                  Fill accurate details as profile details cannot be changed
                  once you apply.
                </div>
              )}
            </div>

            {/* Progress bar */}
            <div className="flex items-center space-x-4">
              <div className="text-left">
                <div className="text-base text-black">75% completed</div>
                <div className="w-48 bg-gray-200 rounded-full h-2 mt-1">
                  <div
                    className="bg-[#572575] h-2 rounded-full"
                    style={{ width: "75%" }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-[0_0_12px_2px_#0000001A]">
            <div className="border-b overflow-x-auto">
              <nav
                className="
      flex 
      px-6 
      space-x-8 
      md:space-x-8 
      sm:space-x-4 
      overflow-x-auto 
      scrollbar-hide
      snap-x
      snap-mandatory
    "
              >
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.name}
                      onClick={() => setActiveTab(tab.name)}
                      className={`
            flex-shrink-0 
            snap-start
            flex items-center 
            py-4 px-3 
            font-medium 
            text-sm md:text-base 
            font-siromi 
            text-gray-500 
            hover:text-gray-700 
            relative
          `}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      {tab.name}
                      {activeTab === tab.name && (
                        <span className="absolute bottom-0 left-0 w-full h-1 bg-[#572575] rounded-t-md" />
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">{renderTabContent()}</div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProfilePage;
